import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { getOrganization, getUser, setLocalStorage, useAuth } from '@vizit/auth';
import { AuthFooter, SsoCallback } from '@vizit/organisms';
import * as React from 'react';
import { useAnalytics } from '../../analytics';

type SsoCallbackSearch = {
  user_id?: string;
  organization_id?: string;
};

export const Route = createFileRoute('/_auth/sso/callback')({
  staticData: {
    pageName: 'SSO Callback',
  },
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>): SsoCallbackSearch => ({
    user_id: typeof search.user_id === 'string' ? search.user_id : undefined,
    organization_id: typeof search.organization_id === 'string' ? search.organization_id : undefined,
  }),
});

function RouteComponent() {
  const navigate = useNavigate();
  const { user_id: userId, organization_id: organizationId } = useSearch({ from: Route.id });
  const { onReauthenticate } = useAuth();
  const { onIdentify } = useAnalytics();

  const handleVerifySession = React.useCallback(async () => {
    if (!userId || !organizationId) {
      return false;
    }

    let userData: Awaited<ReturnType<typeof getUser>>;
    try {
      userData = await getUser(userId);
    } catch {
      return false;
    }

    if (!userData?.email_address) {
      return false;
    }

    let orgData: Awaited<ReturnType<typeof getOrganization>>;
    try {
      orgData = await getOrganization(organizationId);
    } catch {
      return false;
    }

    setLocalStorage({
      user_id: userId,
      organization_id: organizationId,
      organization_name: orgData?.name ?? userData?.default_organization_name ?? '',
      default_portfolio_id: orgData?.default_portfolio_id ?? userData?.default_portfolio_id ?? '',
      default_retailer: orgData?.default_retailer ?? undefined,
      user_email: userData.email_address,
    });

    const success = await onReauthenticate();

    if (success) {
      onIdentify(userId, organizationId, orgData?.name, userData.email_address);
    }

    return success;
  }, [userId, organizationId, onReauthenticate, onIdentify]);

  const handleNavigate = React.useCallback(
    (path: string) => {
      navigate({ to: path });
    },
    [navigate],
  );

  return (
    <div className="h-screen w-screen flex flex-col justify-center items-center">
      <SsoCallback onVerifySession={handleVerifySession} onNavigate={handleNavigate} />
      <AuthFooter />
    </div>
  );
}
